/**
 * Native-shell integration — Capacitor-only code paths.
 *
 * The app ships as a PWA on the web AND as an iOS/Android native shell
 * built with Capacitor. This module exposes a small surface for the
 * web bundle to interact with the native shell when it exists, and
 * no-ops cleanly when it doesn't (i.e. when the user is on the web
 * version at app.safeandsorted.com).
 *
 * All Capacitor imports are isolated here so the rest of the codebase
 * stays platform-agnostic.
 *
 * Detection: Capacitor.isNativePlatform() returns true inside the
 * iOS/Android WebView shells, false in the browser. Plugin methods
 * that are missing just throw — we swallow exceptions so the web
 * bundle never breaks even if the native bridge is half-wired.
 */

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapacitorApp } from '@capacitor/app';

/** True iff we're running inside the iOS or Android native shell. */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Which native platform we're on, if any. */
export function nativePlatform(): 'ios' | 'android' | 'web' {
  try {
    const p = Capacitor.getPlatform();
    if (p === 'ios' || p === 'android') return p;
    return 'web';
  } catch {
    return 'web';
  }
}

/**
 * Hide the native splash screen after the React app has painted.
 * No-op on web. Called once from main.tsx after the initial render.
 *
 * We don't wait for the splash to time out (configured at 2s in
 * capacitor.config.ts) — the moment React's first commit lands we
 * tell the native shell we're ready. On a warm start that's usually
 * <500ms; on a cold install it might be 1.5s. Either way faster than
 * sitting at the static splash for the full 2s.
 */
export async function hideNativeSplashScreen(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    /* Plugin missing or already hidden — safe to swallow. */
  }
}

/**
 * Configure the status bar on native shells.
 *
 * Style.Dark = dark BACKGROUND (so we want light icons on iOS).
 * We use the same slate-navy as the splash so the status bar visually
 * extends the app's brand header at the top of every screen.
 *
 * Called once at app boot via initNativeShell().
 */
async function configureStatusBar(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    if (nativePlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#0F172A' });
    }
  } catch {
    /* iOS doesn't support setBackgroundColor — swallow. */
  }
}

/**
 * Wire the Android hardware back button.
 *
 * Default Capacitor behaviour: tapping the back button exits the app
 * immediately. That's surprising for users who expect "back" to
 * navigate within the app first (e.g. back from /sites/abc to /sites).
 *
 * Better behaviour: if the WebView has back-navigable history, go
 * back. If we're at the root route, fall through to exiting the app
 * (so a double-back closes the app, matching native Android norms).
 */
function wireAndroidBackButton(): void {
  if (!isNativeApp() || nativePlatform() !== 'android') return;
  try {
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // At root — let the back button exit the app as usual.
        CapacitorApp.exitApp();
      }
    });
  } catch {
    /* Plugin missing — swallow. */
  }
}

/**
 * "Email confirmed → back to the app" hand-off.
 *
 * The signup confirmation email opens in Safari/Chrome. That page
 * (/email-confirmed?source=app) opens `safeandsorted://email-confirmed`,
 * which iOS/Android route back into this app. When that URL arrives:
 *  - if the Signup "check your email" screen is still open, it signs the
 *    customer straight in (it still holds the email + password in memory)
 *  - otherwise we drop them on the sign-in page with a friendly note.
 */
type EmailConfirmedHandler = () => void;
const emailConfirmedHandlers = new Set<EmailConfirmedHandler>();

export function onReturnFromEmailConfirm(handler: EmailConfirmedHandler): () => void {
  emailConfirmedHandlers.add(handler);
  return () => { emailConfirmedHandlers.delete(handler); };
}

/** Fires when the app comes back to the foreground (e.g. after the user
 *  confirms their email in Safari and switches back). */
export function onAppResume(handler: () => void): () => void {
  if (!isNativeApp()) return () => {};
  let remove: (() => void) | null = null;
  let cancelled = false;
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) handler();
  })
    .then((h) => { if (cancelled) void h.remove(); else remove = () => void h.remove(); })
    .catch(() => { /* plugin missing */ });
  return () => { cancelled = true; remove?.(); };
}

function wireEmailConfirmedDeepLink(): void {
  try {
    CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      if (!/email-confirmed/.test(url ?? '')) return;
      if (emailConfirmedHandlers.size > 0) {
        emailConfirmedHandlers.forEach((h) => h());
      } else {
        window.location.assign('/login?confirmed=1');
      }
    });
  } catch {
    /* Plugin missing — swallow. */
  }
}

/**
 * One-shot boot. Call from a top-level effect (or App.tsx mount) to
 * set up the status bar and back button handling. Idempotent —
 * subsequent calls are no-ops.
 */
let booted = false;
export function initNativeShell(): void {
  if (booted) return;
  booted = true;
  if (!isNativeApp()) return;
  void configureStatusBar();
  wireAndroidBackButton();
  wireEmailConfirmedDeepLink();
}
